import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { AccommodationService } from '@lx/quote-tool/shared/data-access/products';
import {
  calculateEndDate,
  filterOnlyCoursesFromQuoteProducts,
  getTuitionWeeks,
  getValidCourses,
  hasIncompleteCourses,
  isAccommodationMandatory,
} from '@lx/quote-tool/shared/data-access/quote';

import {
  AccommodationDatesSectionPayload,
  AccommodationQuoteInput,
  CourseQuoteInput,
  DatePickerFieldDate,
  noAccommodationOption,
  QuoteInput,
  QuoteOutput,
  QuoteProduct,
  Vacation,
  VacationPeriod,
} from '@lx/quote-tool/shared/types';
import { ProductAccommodation } from '@lx/shared/data-access/products';
import {
  addWeeks,
  deepClone,
  findFurthestAvailableDay,
  getDateFromString,
  getDisabledDaysForRange,
  getCurrentOrPreviousFriday,
  isDateAfter,
  isDateWithinRangeWithoutTime,
  toUtc,
  updateToClosestTargetDay,
  updateToNextDate,
  weeksDiff,
} from '@lx/shared/utils';
import { combineLatest, EMPTY, Observable, of, switchMap } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { DateFormat } from '@lx/shared/i18n';
import { SchoolTypes } from '@lx/shared/data-access/types';
import { StepService } from '@lx/quote-tool/shared/services/step-service';
import {
  calculateLimitedAccommodationMaxDateForSchoolYear,
  calculateLimitedMinDateForSchoolYear,
  removeDuplicateAccommodations,
} from '../../../shared/functions';
import { AccommodationAvailability, AccommodationOptions, AccommodationCourseOpenPeriod } from '../../../shared/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@lx/shared/logger';

const DEFAULT_MAX_WEEKS = 52;
const YEARS_IN_FUTURE = 2;

@Component({
  selector: 'lx-accommodation-dates-form-section',
  templateUrl: './accommodation-dates.component.html',
  styleUrls: ['./accommodation-dates.component.scss'],
})
export class AccommodationDatesFormSectionComponent implements OnInit {
  @Input() sectionIndex: string | number;

  protected loading: boolean;
  protected sectionModel: AccommodationDatesSectionPayload = {} as AccommodationDatesSectionPayload;
  protected accommodationOptions: AccommodationOptions[];
  protected accommodationsWithEmptyOption: ProductAccommodation[];
  protected selectedCourses: CourseQuoteInput[];
  protected incompletedCourse: boolean;
  protected accommodationMandatory: boolean;
  protected DateFormat = DateFormat;
  // Temporary workaround: Used to force date picker re-renders due to calendar refreshing issues.
  protected datePickerRenderKey: Record<number, number> = {};
  protected readonly selectPlaceholder: string = $localize`— Please select  —`;

  private accommodations: ProductAccommodation[];
  private noAccommodationOption: ProductAccommodation = {
    ...noAccommodationOption,
    name: $localize`Without accommodation`,
  };
  private tuitionWeeks = 0;
  private selectedSchool: string;
  private endDateLastCourse: Date;
  private schoolType: SchoolTypes;
  private vacationPeriods: Array<VacationPeriod>;
  private quoteOutputCourses: QuoteProduct[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly stepService = inject(StepService);
  private readonly accommodationService = inject(AccommodationService);
  private readonly loggerService = inject(LoggerService);
  // Temporary workaround: Stores timeout IDs used to force date picker re-renders due to calendar refreshing issues.
  private readonly datePickerTimeoutIds = new Map<number, number>();

  ngOnInit() {
    // Pass storeChanges=false to prevent overwriting stored accommodations before prepareLoadStoredData runs
    this.resetAccommodations(undefined, undefined, false);
    this.prepareLoadStoredData();
    this.prepareResetOnChanges();
  }

  private calculateAccommodationDisabledDays(accommodation: ProductAccommodation): number[] {
    let disabledDays: number[] = [];

    if (accommodation && this.selectedCourses.length) {
      const allWeekdays = [0, 1, 2, 3, 4, 5, 6];
      const startingDaysOfTheWeek = accommodation.startingDaysOfTheWeek;
      // calendar expects dates with sunday as starting date.
      disabledDays = allWeekdays.filter((value) => !startingDaysOfTheWeek.includes(value));
    }

    return disabledDays;
  }

  private calculateAccommodationDisabledDates(accommodation?: ProductAccommodation): Date[] {
    if (!accommodation) {
      return this.vacationPeriods.map((vacation) => getDisabledDaysForRange(vacation.startDate, vacation.endDate)).flat();
    }

    return this.vacationPeriods
      .map((vacation) => this.normalizeWithAccommodation(vacation, accommodation.startingDaysOfTheWeek, accommodation.endingDaysOfTheWeek))
      .map((vacation) => getDisabledDaysForRange(vacation.startDate, vacation.endDate))
      .flat();
  }

  private calculateMinDate(index: number, selectedAccommodation?: ProductAccommodation): Date {
    const limitDateSchoolYear = this.getLimitMinDateSchoolYear(selectedAccommodation);
    const limitMaxDateSchoolYear = this.getLimitMaxDateSchoolYear(selectedAccommodation);
    const previousAccommodation = this.sectionModel.accommodations[index - 1];

    let startDate: Date;
    let firstSelectedCourseStartDate: Date;
    if (index === 0) {
      firstSelectedCourseStartDate = this.getFirstSelectedCourseStartDate();
      startDate = firstSelectedCourseStartDate ? addWeeks(firstSelectedCourseStartDate, -1) : undefined;
    } else if (selectedAccommodation) {
      startDate = this.calculateStartDateFromAccommodation(previousAccommodation, this.getAccommodationByIndex(index - 1));
    } else {
      const previousAccommodation = this.sectionModel.accommodations[index - 1];
      startDate =
        previousAccommodation.startDate && previousAccommodation.weeks
          ? addWeeks(getDateFromString(previousAccommodation.startDate), previousAccommodation.weeks, [], false)
          : undefined;
    }

    const date = calculateLimitedMinDateForSchoolYear(limitDateSchoolYear, limitMaxDateSchoolYear, startDate, firstSelectedCourseStartDate);
    if (!selectedAccommodation) {
      return date;
    }

    const accommodationMinDate = this.getMinDateWithVacations(selectedAccommodation, date, index);

    // if accommodation has an availability period, minDate must respect it
    const availability = this.accommodationOptions[index]?.availability;
    if (availability?.length && isDateAfter(availability[0].from, accommodationMinDate)) {
      return availability[0].from;
    }

    return accommodationMinDate;
  }

  private calculateMaxDate(index: number, selectedAccommodation: ProductAccommodation): Date {
    if (this.accommodationMandatory) {
      return this.getFirstSelectedCourseStartDate();
    } else {
      const limitDateSchoolYear: Date = this.getLimitMaxDateSchoolYear(selectedAccommodation);
      const availability = this.accommodationOptions[index]?.availability;

      if (selectedAccommodation?.durationMaxUnbounded) {
        const firstSelectedCourseStartDate = this.getFirstSelectedCourseStartDate();
        const maxDateForDefaultMaxWeeks = addWeeks(firstSelectedCourseStartDate, DEFAULT_MAX_WEEKS);

        return calculateLimitedAccommodationMaxDateForSchoolYear(limitDateSchoolYear, maxDateForDefaultMaxWeeks, 0, availability);
      }

      const selectedDuration = this.sectionModel.accommodations[index]?.weeks ?? 0;
      const maxDateWithVacations = this.getMaxDateWithVacations(selectedDuration, index, selectedAccommodation);

      return calculateLimitedAccommodationMaxDateForSchoolYear(limitDateSchoolYear, maxDateWithVacations, selectedDuration, availability);
    }
  }

  private calculateStartDateFromAccommodation(
    { startDate, weeks }: AccommodationQuoteInput,
    { startingDaysOfTheWeek, endingDaysOfTheWeek }: ProductAccommodation,
  ): Date {
    const vacationPeriods = this.vacationPeriods.map((vacation) => this.normalizeWithAccommodation(vacation, startingDaysOfTheWeek, endingDaysOfTheWeek));

    const prevEndDate = this.calculateEndDateWithVacations(getDateFromString(startDate), weeks, vacationPeriods);
    return updateToClosestTargetDay(startingDaysOfTheWeek, prevEndDate);
  }

  private getMinDateWithVacations(accommodation: ProductAccommodation, initialDate: Date, index: number): Date {
    const minDate = updateToNextDate(accommodation?.startingDaysOfTheWeek, initialDate);
    if (index === 0) {
      return minDate;
    }
    // consider possible vacation periods between previous accommodation and duration
    let vacationWeeks = 0;
    const vacationPeriods = this.vacationPeriods.map((vacation) =>
      this.normalizeWithAccommodation(vacation, accommodation.startingDaysOfTheWeek, accommodation.endingDaysOfTheWeek),
    );
    if (vacationPeriods.some((vacation) => isDateWithinRangeWithoutTime(minDate, [vacation.startDate, vacation.endDate]))) {
      const previousAccommodation = this.sectionModel.accommodations[index - 1];
      const previousStartDate = getDateFromString(previousAccommodation.startDate);
      const previousDuration = previousAccommodation.weeks;
      const previousEndDate = addWeeks(previousStartDate, previousDuration);
      vacationWeeks = this.getVacationWeeksWithinDates(previousStartDate, previousEndDate, vacationPeriods);
    }

    return addWeeks(minDate, vacationWeeks);
  }

  private getMaxDateWithVacations(selectedDuration: number, index: number, accommodation: ProductAccommodation): Date {
    // consider last accommodation booking based on last courses end date.
    const lastCourseDate = this.getLastCourseDateOrLimitDate(accommodation);
    const lastCourseDateUtc = toUtc(lastCourseDate);
    const maxDate = selectedDuration ? addWeeks(lastCourseDateUtc, -selectedDuration) : lastCourseDateUtc;
    const upcomingVacationPeriods = this.vacationPeriods.filter((vacation) => maxDate.getTime() < vacation.startDate.getTime());
    const selectedAccommodation = this.getAccommodationByIndex(index);
    const minDateUtc = toUtc(this.calculateMinDate(index, selectedAccommodation));
    const vacationWeeks = this.getVacationWeeksWithinDates(minDateUtc, lastCourseDateUtc, upcomingVacationPeriods);
    return selectedDuration ? addWeeks(toUtc(lastCourseDateUtc), -(selectedDuration + vacationWeeks)) : lastCourseDateUtc;
  }

  private getLastCourseDateOrLimitDate(accommodation: ProductAccommodation): Date {
    if (this.shouldDateBeLimitedToEndOfYear(accommodation)) {
      /**
       * During year shift period when prices for current and next year are available
       * some products might not be available anymore for next year
       * hence the availability in the quote tool should be limited to the current year.
       *
       */
      return new Date(this.getCurrentYear(), 11, 31); // Last day of the current year
    }

    /**
     * Otherwise quote tool should take the last selected course end date
     */
    return this.getLastCourseDate(accommodation);
  }

  private shouldDateBeLimitedToEndOfYear(accommodation: ProductAccommodation): boolean {
    /**
     * Date should be limited to end of the year if:
     * * product next year prices are available ( school ready for year shift )
     * * product availability is only for current year ( product validity = current year )
     */
    const { inPriceShiftPeriod, schoolYears, schoolYear } = accommodation || {};
    const currentYear = this.getCurrentYear();
    return inPriceShiftPeriod && schoolYears?.length == 1 && schoolYear === currentYear;
  }

  private getLimitMinDateSchoolYear(selectedAccommodation: ProductAccommodation): Date {
    if (selectedAccommodation) {
      const minDate = new Date(Math.min(...selectedAccommodation.schoolYears), 0, 1);
      // vacations that may impact the min date only. Those that are after the min date will be handled when calculating the duration.
      const vacationPeriod = this.getNormalizedVacationPeriods(minDate, selectedAccommodation).reduce((previousValue, currentValue) => {
        return previousValue && previousValue.endDate.getTime() >= currentValue.endDate.getTime() ? previousValue : currentValue;
      }, undefined);

      return vacationPeriod ? vacationPeriod.endDate : minDate;
    }
  }

  private getLimitMaxDateSchoolYear(selectedAccommodation: ProductAccommodation): Date {
    if (selectedAccommodation) {
      const maxDate = new Date(Math.max(...selectedAccommodation.schoolYears) + YEARS_IN_FUTURE, 11, 31);
      // vacations that may impact the min date only. Those that are after the min date will be handled when calculating the duration.
      const vacationPeriod = this.getNormalizedVacationPeriods(maxDate, selectedAccommodation).reduce((previousValue, currentValue) => {
        return previousValue && previousValue.startDate.getTime() <= currentValue.startDate.getTime() ? previousValue : currentValue;
      }, undefined);

      return vacationPeriod ? vacationPeriod.startDate : maxDate;
    }
  }

  private getVacationWeeksWithinDates(startDate: Date, endDate: Date, vacationPeriods: Array<VacationPeriod>) {
    //retrieves number of full weeks found within start and end date range
    return vacationPeriods
      .filter(
        (vacation) =>
          isDateWithinRangeWithoutTime(vacation.startDate, [startDate, endDate]) || isDateWithinRangeWithoutTime(vacation.endDate, [startDate, endDate]),
      )
      .reduce((previousValue, currentValue) => previousValue + currentValue.weeks, 0);
  }

  private getNormalizedVacationPeriods(date: Date, accommodation: ProductAccommodation) {
    return this.vacationPeriods
      .filter((vacation) => isDateWithinRangeWithoutTime(date, [vacation.startDate, vacation.endDate]))
      .map((vacation) => this.normalizeWithAccommodation(vacation, accommodation.startingDaysOfTheWeek, accommodation.endingDaysOfTheWeek));
  }

  private normalizeWithAccommodation(vacation: VacationPeriod, startingDaysOfTheWeek: Array<number>, endingDaysOfTheWeek: Array<number>) {
    //updates vacations taking into account accommodation starting and ending day of the week
    return {
      startDate: updateToClosestTargetDay(startingDaysOfTheWeek, vacation.startDate),
      endDate: updateToClosestTargetDay(endingDaysOfTheWeek, vacation.endDate),
      weeks: vacation.weeks,
    };
  }

  private calculateDurationMin(selectedAccommodation: ProductAccommodation): number {
    return this.accommodationMandatory ? this.tuitionWeeks : selectedAccommodation?.durationMin;
  }

  private calculateEndDateWithVacations(date: Date, duration: number, vacationPeriods: Array<VacationPeriod>) {
    if (duration < 1) {
      return date;
    }

    if (vacationPeriods.some((vacation) => isDateWithinRangeWithoutTime(date, [vacation.startDate, vacation.endDate]))) {
      return this.calculateEndDateWithVacations(addWeeks(date, 1), duration, vacationPeriods);
    }

    return this.calculateEndDateWithVacations(addWeeks(date, 1), duration - 1, vacationPeriods);
  }

  private getLastSelectedCourse(): CourseQuoteInput {
    return this.selectedCourses[this.selectedCourses.length - 1];
  }

  private getFirstSelectedCourseStartDate(): Date {
    return getDateFromString(this.selectedCourses[0]?.startDate);
  }

  private removeAccommodationsAfter(index: number, storeChanges = true) {
    for (let i = this.sectionModel.accommodations?.length - 1; i > index; i--) {
      this.removeAccommodationItem(i, false);
    }

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  private fillAccommodationList(accommodationList: ProductAccommodation[] = [], storeChanges = true) {
    this.accommodations = accommodationList
      .filter((accommodation: ProductAccommodation) => accommodation.durationMin <= this.tuitionWeeks)
      .filter((accommodation: ProductAccommodation) => {
        return accommodation.durationMin <= this.calculateDurationMax(0, accommodation);
      })
      .filter((accommodation: ProductAccommodation) => this.isOpenDuringAnyCoursePeriod(accommodation));

    // Add "Without accommodation" option if necessary.
    if (this.hasNoAccommodationOption()) {
      this.accommodationsWithEmptyOption = [
        this.noAccommodationOption,
        ...this.accommodations.map((accommodation: ProductAccommodation) => ({
          ...accommodation,
          weight: this.noAccommodationOption.weight + 1,
        })),
      ];
    } else {
      this.accommodationsWithEmptyOption = this.accommodations;
    }

    this.accommodationChanged(0, undefined, false);

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  /**
   * Recover previously stored data (user filled data). Runs only once with the first emit of the quote input and output.
   */
  private prepareLoadStoredData() {
    this.stepService
      .requestActiveQuote()
      .pipe(
        switchMap((quoteOutput: QuoteOutput) => combineLatest([of(quoteOutput), this.loadSchoolAccommodations(quoteOutput?.input)])),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([quoteOutput, loadedAccommodations]: [QuoteOutput, ProductAccommodation[][]]) => {
        const { input } = quoteOutput;
        const { accommodations } = input;

        const accommodationList: ProductAccommodation[] = removeDuplicateAccommodations(loadedAccommodations.flat());

        // Pass storeChanges=false to prevent overwriting stored data before restoration completes
        this.resetAccommodations(input, accommodationList, false);
        this.quoteOutputCourses = filterOnlyCoursesFromQuoteProducts(quoteOutput.products);

        if (accommodations?.length) {
          accommodations.forEach(({ code, weeks, startDate }: AccommodationQuoteInput, index: number) => {
            this.addAccommodationItem(index, false);
            this.accommodationChanged(index, code, false);
            this.durationChange(index, weeks, false);
            let startDateObj = undefined;
            if (startDate) {
              startDateObj = {
                dateString: startDate,
                dateObject: getDateFromString(startDate),
              };
            }
            this.accommodationStartDateChange(index, startDateObj, false);
          });
        }
        this.storeSectionData();
      });
  }

  private loadSchoolAccommodations(quoteInput: QuoteInput): Observable<ProductAccommodation[][]> {
    const newSchool: string = quoteInput.school;
    const newSelectedCourses: CourseQuoteInput[] = getValidCourses(quoteInput);
    const validSchoolAndCourse = !isNaN(+newSchool) && newSelectedCourses?.some((course: CourseQuoteInput) => !isNaN(+course?.code) && course.weeks > 0);
    if (!validSchoolAndCourse) {
      return of([]);
    }
    this.loading = true;
    return combineLatest(
      newSelectedCourses.map((course: CourseQuoteInput) => this.accommodationService.getByCourse(newSchool, course?.code, course?.startDate)),
    ).pipe(
      tap(() => (this.loading = false)),
      catchError((error) => {
        this.loading = false;
        this.loggerService.error(error);
        this.resetAccommodations(quoteInput);
        return EMPTY;
      }),
    );
  }

  /**
   * Resets all the accommodations data and related fields in the section model, re-filling it again with up-to-date data.
   */
  private resetAccommodations(quoteInput?: QuoteInput, accommodationList?: ProductAccommodation[], storeChanges = true) {
    // 1. Store previous sections selected values
    this.schoolType = quoteInput?.schoolType;
    this.selectedSchool = quoteInput?.school;
    this.selectedCourses = deepClone(getValidCourses(quoteInput));
    this.tuitionWeeks = getTuitionWeeks(quoteInput);
    this.accommodationMandatory = isAccommodationMandatory(quoteInput);
    this.incompletedCourse = hasIncompleteCourses(quoteInput);
    this.endDateLastCourse = this.selectedCourses.length && calculateEndDate(this.getLastSelectedCourse());
    this.vacationPeriods = this.extractVacationsFromAccommodation(accommodationList);

    // 2. Clear accommodations
    this.sectionModel.accommodations = null;
    this.accommodationOptions = null;
    this.addAccommodationItem(0, false);

    // 3. Reset section fields
    this.fillAccommodationList(accommodationList, storeChanges);
    this.resetDateOptions(0);
  }

  private resetDateOptions(index: number) {
    const selectedAccommodation = this.getAccommodationByIndex(index);
    const accommodationOption = this.accommodationOptions[index];

    accommodationOption.availability = this.buildAccommodationAvailability(selectedAccommodation);
    accommodationOption.minDate = this.calculateMinDate(index, selectedAccommodation);
    accommodationOption.maxDate = this.calculateMaxDate(index, selectedAccommodation);
    accommodationOption.disabledDays = this.calculateAccommodationDisabledDays(selectedAccommodation);
    accommodationOption.disabledDates = this.calculateAccommodationDisabledDates(selectedAccommodation);
    accommodationOption.durationMin = this.calculateDurationMin(selectedAccommodation);
    accommodationOption.durationMax = this.calculateDurationMax(index, selectedAccommodation);
  }

  /**
   * Prepares the subscription for when the active quote changes. Once there is a change on the
   * quote, we check if the accommodations should be reloaded, on that case, we call to
   * the service that loads again the accommodations. With this information + the updated quote
   * the accommodations are reset accordingly.
   *
   * We are listening to changes in the quote output
   * so we can determine the correct end date for courses which can only be calculated by the back-end.
   */
  private prepareResetOnChanges() {
    this.stepService
      .requestActiveQuote()
      .pipe(
        switchMap((quoteOutput: QuoteOutput) => {
          const { input: quoteInput } = quoteOutput;
          const { school: newSchoolCode, schoolType } = quoteInput;
          const newSelectedCourses: CourseQuoteInput[] = getValidCourses(quoteInput);
          const requiredFieldsPresent = newSchoolCode && newSelectedCourses?.length;
          const schoolChanged = this.selectedSchool !== newSchoolCode;
          const schoolTypeChanged = this.schoolType !== schoolType;
          const courseDataChanged = this.hasCourseChanged(newSelectedCourses);
          const accommodationsReloadRequired = requiredFieldsPresent && (schoolChanged || schoolTypeChanged || courseDataChanged);

          if (!accommodationsReloadRequired) {
            return combineLatest([of(quoteOutput), of([])]);
          }

          return combineLatest([of(quoteOutput), this.loadSchoolAccommodations(quoteInput)]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([quoteOutput, loadedAccommodationLists]: [QuoteOutput, ProductAccommodation[][]]) => {
        const { input: quoteInput } = quoteOutput;
        const { school: newSchoolCode, schoolType } = quoteInput;

        const schoolChanged = this.selectedSchool !== newSchoolCode;
        const schoolTypeChanged = this.schoolType !== schoolType;

        // Save quote products so we can get back-end data related to the course.
        const quoteProducts = filterOnlyCoursesFromQuoteProducts(quoteOutput?.products);
        if (quoteProducts) {
          this.quoteOutputCourses = quoteProducts;
        }

        // If the school has changed, re-fill accommodations list.
        if (schoolChanged || schoolTypeChanged) {
          this.schoolType = schoolType;
          this.fillAccommodationList();
        }

        // Only reset accommodations when a new course was added but not completed yet, if course has been completed and there
        // were changes to the quote, we will reset accommodations from loaded ones
        if (hasIncompleteCourses(quoteInput)) {
          this.resetAccommodations(quoteInput);
        } else if (loadedAccommodationLists?.length) {
          const accommodationList: ProductAccommodation[] = removeDuplicateAccommodations(loadedAccommodationLists.flat());
          this.resetAccommodations(quoteInput, accommodationList);
        }
      });
  }

  private hasCourseChanged(newCourses: CourseQuoteInput[]): boolean {
    return (
      this.selectedCourses?.length !== newCourses?.length ||
      newCourses?.some(
        (newCourse: CourseQuoteInput, index: number) =>
          newCourse.code !== this.selectedCourses[index].code ||
          newCourse.startDate !== this.selectedCourses[index].startDate ||
          newCourse.weeks !== this.selectedCourses[index].weeks,
      )
    );
  }

  private extractVacationsFromAccommodation(accommodationList?: ProductAccommodation[]) {
    if (!accommodationList?.length) {
      return [];
    }

    return this.convertVacations(accommodationList[0].vacations ?? []);
  }

  private convertVacations(vacations?: Array<Vacation>): Array<VacationPeriod> {
    return vacations
      .filter((courseVacation) => courseVacation.durationWeeks > 0)
      .map(({ startDate, endDate, durationWeeks }) => {
        return {
          startDate: getDateFromString(startDate),
          endDate: getDateFromString(endDate),
          weeks: durationWeeks,
        };
      });
  }

  private getAccommodationByIndex(index: number): ProductAccommodation {
    const code = this.sectionModel.accommodations[index]?.code;
    return code ? this.accommodations?.find((item) => item.code === code) : null;
  }

  private storeSectionData() {
    this.stepService.updatePartialRequest<AccommodationDatesSectionPayload>(this.sectionModel);
  }

  protected accommodationChanged(index: number, newAccommodationCode?: string, storeChanges = true) {
    const targetAccommodation = this.sectionModel.accommodations?.[index];
    if (!targetAccommodation) {
      return;
    }

    const withoutAccommodationSelected = newAccommodationCode === this.noAccommodationOption.code;
    const accommodationChanged = targetAccommodation.code !== newAccommodationCode;

    if (withoutAccommodationSelected) {
      targetAccommodation.code = newAccommodationCode;
      targetAccommodation.weeks = undefined;
      this.accommodationStartDateChange(index, undefined, false);

      if (storeChanges) {
        this.storeSectionData();
      }
    } else if (accommodationChanged) {
      targetAccommodation.code = newAccommodationCode;
      this.durationChange(index, undefined, false);
      this.resetDateOptions(index);

      if (storeChanges) {
        this.storeSectionData();
      }
    }

    // Recalculate availability and max duration for the accommodation if not fully open.
    const productAccommodation = this.getProductAccommodationByCode(newAccommodationCode);
    const fullyOpenAccommodation = this.isFullyOpenDuringAnyCoursePeriod(productAccommodation);
    if (!fullyOpenAccommodation) {
      this.accommodationOptions[index].availability = this.buildAccommodationAvailability(productAccommodation);
      this.accommodationOptions[index].durationMax = this.calculateDurationMax(index, productAccommodation);
    }
  }

  protected calculateDurationMax(index: number, selectedAccommodation?: ProductAccommodation): number {
    if (selectedAccommodation?.durationMaxUnbounded) {
      return DEFAULT_MAX_WEEKS;
    }

    if (this.accommodationMandatory && index === 0) {
      return this.tuitionWeeks;
    }

    const tuitionWeeks = this.tuitionWeeks ?? 0;
    const maxAccommodationWeeks = selectedAccommodation?.durationMax ?? DEFAULT_MAX_WEEKS;
    const weeksDiffForAccommodation = weeksDiff(this.calculateMinDate(index, selectedAccommodation), this.calculateMaxDate(index, selectedAccommodation)) ?? 0;
    let maxWeeksForCurrentIndex = tuitionWeeks;
    for (let i = 0; i < index; i++) {
      maxWeeksForCurrentIndex -= this.sectionModel.accommodations[i].weeks ?? 0;
    }

    return Math.min(tuitionWeeks, maxAccommodationWeeks, maxWeeksForCurrentIndex, weeksDiffForAccommodation);
  }

  private hasNoAccommodationOption(): boolean {
    return !this.accommodationMandatory || this.accommodations.length < 1;
  }

  protected hasButtons(): boolean {
    return !this.accommodationMandatory && !this.isWithoutAccommodation(this.sectionModel.accommodations[0]);
  }

  protected isWithoutAccommodation(accommodationItemToCheck: AccommodationQuoteInput): boolean {
    return accommodationItemToCheck?.code === this.noAccommodationOption.code;
  }

  protected addAccommodationItem(index?: number, storeChanges = true) {
    if (!this.sectionModel?.accommodations?.length) {
      this.sectionModel.accommodations = [];
      this.accommodationOptions = [];
    }

    const newAccommodationInput = {} as AccommodationQuoteInput;
    const newAccommodationOptions = {} as AccommodationOptions;

    if (index >= 0 && !storeChanges) {
      this.sectionModel.accommodations[index] = newAccommodationInput;
      this.accommodationOptions[index] = newAccommodationOptions;
    } else {
      this.sectionModel.accommodations.push(newAccommodationInput);
      this.accommodationOptions.push(newAccommodationOptions);
      this.accommodationOptions[index].items = this.getAccommodationList(index);
    }

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  // Remove any accommodation with an invalid duration period.
  private getAccommodationList(index: number) {
    if (index === 0) {
      return this.accommodations;
    }
    return this.accommodations?.filter((accommodation: ProductAccommodation) => accommodation.durationMin <= this.calculateDurationMax(index, accommodation));
  }

  protected removeAccommodationItem(index: number, storeChanges = true) {
    this.sectionModel.accommodations.splice(index, 1);
    for (let i = index; i < this.sectionModel.accommodations.length; i++) {
      this.resetDateOptions(index);
    }

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  protected durationChange(index: number, duration: number, storeChanges = true) {
    const targetAccommodation = this.sectionModel.accommodations[index];
    if (!targetAccommodation || targetAccommodation.weeks === duration) {
      return;
    }

    targetAccommodation.weeks = duration;
    const selectedAccommodation = this.getAccommodationByIndex(index);
    // We need to refresh the minDate object for the date-picker to detect changes and show the min month to the user.
    this.accommodationOptions[index].minDate = new Date(this.accommodationOptions[index].minDate);
    this.accommodationOptions[index].maxDate = this.calculateMaxDate(index, selectedAccommodation);

    const maxDate = this.accommodationOptions[index].maxDate;
    const startDate = getDateFromString(this.sectionModel.accommodations[index].startDate);

    if (startDate && startDate > maxDate) {
      this.resetAccommodationStartDate(index, storeChanges);
    }

    // Changing the duration of an accommodation can invalidate subsequent accommodations
    // because their min/max dates are derived from earlier weeks. To avoid leaving
    // invalid or overlapping accommodations in the model, remove all entries after
    // the changed index and then persist the updated state once if requested.
    for (let i = this.sectionModel.accommodations.length - 1; i > index; i--) {
      this.removeAccommodationItem(i, false);
    }

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  /**
   * Resets the accommodation start date and forces the date picker to re-render.
   */
  private resetAccommodationStartDate(index: number, storeChanges = true): void {
    this.accommodationStartDateChange(index, undefined, storeChanges);
    this.forceRenderDatePicker(index);
  }

  /**
   * Forces the date picker component at the given index to re-render by toggling its visibility.
   * Any previously scheduled re-render for the same index is cancelled.
   */
  private forceRenderDatePicker(index: number): void {
    clearTimeout(this.datePickerTimeoutIds.get(index));

    this.datePickerRenderKey[index] = -1;
    this.changeDetectorRef.detectChanges();

    this.datePickerTimeoutIds.set(
      index,
      window.setTimeout(() => {
        this.datePickerRenderKey[index] = 0;
        this.datePickerTimeoutIds.delete(index);
        this.changeDetectorRef.detectChanges();
      }),
    );
  }

  protected accommodationStartDateChange(index: number, aDate: DatePickerFieldDate, storeChanges = true) {
    this.sectionModel.accommodations[index].startDate = aDate?.dateString ?? null;
    this.sectionModel.accommodations[index].startDateObject = aDate?.dateObject ?? null;

    // Remove next accommodations
    this.removeAccommodationsAfter(index, false);

    if (storeChanges) {
      this.storeSectionData();
    }
  }

  private isOpenDuringAnyCoursePeriod(accommodation: ProductAccommodation): boolean {
    return accommodation.openingPeriods?.length == 0 || this.getOpeningPerCoursePeriod(accommodation).length > 0;
  }

  private isFullyOpenDuringAnyCoursePeriod(accommodation: ProductAccommodation): boolean {
    const firstSelectedCourseStartDate = this.getFirstSelectedCourseStartDate();
    return (
      accommodation?.openingPeriods.length == 0 ||
      accommodation?.openingPeriods.find((openingPeriod) => {
        const dateRange: [Date, Date] = [getDateFromString(openingPeriod.from), getDateFromString(openingPeriod.until)];
        return isDateWithinRangeWithoutTime(firstSelectedCourseStartDate, dateRange) && isDateWithinRangeWithoutTime(this.endDateLastCourse, dateRange);
      }) != undefined
    );
  }

  private getOpeningPerCoursePeriod(accommodation: ProductAccommodation): Array<AccommodationCourseOpenPeriod> {
    return this.selectedCourses
      .map((course) => {
        const openPeriod = accommodation?.openingPeriods.find((openingPeriod) => {
          const courseStartDate = getDateFromString(course.startDate);
          const courseEndDate = this.getQuoteProductEndDate(course);

          const dateRange: [Date, Date] = [getDateFromString(openingPeriod.from), getDateFromString(openingPeriod.until)];
          return isDateWithinRangeWithoutTime(courseStartDate, dateRange) || isDateWithinRangeWithoutTime(courseEndDate, dateRange);
        });
        return {
          openPeriod,
          course,
        };
      })
      .filter((accommodationDatePeriod) => accommodationDatePeriod.openPeriod !== undefined);
  }

  /**
   * Gets the real course endDate from the back-end date or fallbacks to the last friday before the course end date.
   * The fallback is there just as a safeguard in case we still don't have the back-end date available.
   */
  private getQuoteProductEndDate(course: CourseQuoteInput) {
    const quoteProduct = this.quoteOutputCourses.find((product) => product.code === course.code);

    if (quoteProduct && quoteProduct.endDate) {
      return getDateFromString(quoteProduct.endDate);
    }

    // Fallback to using the last friday as the course end date.
    return getCurrentOrPreviousFriday(calculateEndDate(course));
  }

  private getProductAccommodationByCode(accommodationCode: string): ProductAccommodation {
    return this.accommodations.find((accommodation) => accommodation.code === accommodationCode);
  }

  private buildAccommodationAvailability(productAccommodation: ProductAccommodation): AccommodationAvailability[] {
    if (!productAccommodation) {
      return [];
    }

    const accommodationOpenPeriods = this.getOpeningPerCoursePeriod(productAccommodation);
    const { startingDaysOfTheWeek, endingDaysOfTheWeek } = productAccommodation;

    return (accommodationOpenPeriods ?? [])
      .map((openPeriod) => this.calculateAccommodationAvailability(openPeriod, startingDaysOfTheWeek, endingDaysOfTheWeek))
      .filter((availability) => !!availability);
  }

  private calculateAccommodationAvailability(
    openPeriod: AccommodationCourseOpenPeriod,
    startingDaysOfTheWeek: number[],
    endingDaysOfTheWeek: number[],
  ): AccommodationAvailability | undefined {
    const { course } = openPeriod;

    const startCourseDate = getDateFromString(course.startDate);
    const endCourseDate = this.getQuoteProductEndDate(course);

    const openPeriodFrom = getDateFromString(openPeriod.openPeriod.from);
    const openPeriodUntil = getDateFromString(openPeriod.openPeriod.until);

    const dateRange: [Date, Date] = [openPeriodFrom, openPeriodUntil];
    const startDateWithinRange = isDateWithinRangeWithoutTime(startCourseDate, dateRange);
    const endDateWithinRange = isDateWithinRangeWithoutTime(endCourseDate, dateRange);

    // If the accommodation is open for the whole course period, no need to include the range on availability periods.
    if (startDateWithinRange && endDateWithinRange) {
      return undefined;
    }

    if (startDateWithinRange) {
      return {
        from: findFurthestAvailableDay(startCourseDate, startingDaysOfTheWeek, 'backward'),
        until: openPeriodUntil,
      };
    } else if (endDateWithinRange) {
      return {
        from: openPeriodFrom,
        until: findFurthestAvailableDay(endCourseDate, endingDaysOfTheWeek, 'forward'),
      };
    }

    return undefined;
  }

  private getLastCourseDate(accommodation: ProductAccommodation) {
    if (!this.endDateLastCourse) {
      return;
    }

    const lastCourseStartDate = getDateFromString(this.getLastSelectedCourse().startDate);
    const vacationWeeks = this.getVacationWeeksWithinDates(lastCourseStartDate, this.endDateLastCourse, this.vacationPeriods);
    const lastCourseEndDate = addWeeks(this.endDateLastCourse, vacationWeeks);

    return accommodation ? updateToClosestTargetDay(accommodation.startingDaysOfTheWeek, lastCourseEndDate) : lastCourseEndDate;
  }

  private getCurrentYear(): number {
    return new Date().getFullYear();
  }
}
